import { FC, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import Routes from '@/router/Routes.ts';
import useAdminCarers from '@/api/useAdminCarers.ts';
import useAdminCarerOnboarding from '@/api/useAdminCarerOnboarding.ts';
import useUpdateVerification from '@/api/useUpdateVerification.ts';
import useAdminUpdateCarerProfile from '@/api/useAdminUpdateCarerProfile.ts';
import useAdminToggleCarerVisibility from '@/api/useAdminToggleCarerVisibility.ts';
import { formatCareType, formatSkill } from '@/utils/formatCareType.ts';
import { CARE_TYPE_OPTIONS, SKILL_CATEGORIES } from '@/constants/careSkills.ts';
import { Button, Alert } from 'react-daisyui';
import ApproveCarerModal from '@/components/modals/ApproveCarerModal.tsx';
import RejectCarerModal from '@/components/modals/RejectCarerModal.tsx';
import DeleteUserModal from '@/components/modals/DeleteUserModal.tsx';
import useAdminDeleteUser from '@/api/useAdminDeleteUser.ts';
import supabaseClient from '@/api/supabaseClient.ts';


const AdminCarerDetailsPage: FC = () => {
  const { carerId } = useParams<{ carerId: string }>();
  const navigate = useNavigate();
  const { data: carers, isLoading, isError } = useAdminCarers();
  const { data: onboarding, isLoading: isLoadingOnboarding } = useAdminCarerOnboarding(carerId);
  const { mutate: updateVerification, isPending: isVerifying } = useUpdateVerification();
  const { mutate: updateProfile, isPending: isSavingProfile, isSuccess: isSaveSuccess, isError: isSaveError, error: saveError } = useAdminUpdateCarerProfile();
  const { mutate: toggleVisibility, isPending: isTogglingVisibility } = useAdminToggleCarerVisibility();
  const { mutate: deleteUser, isPending: isDeleting, isError: isDeleteError, error: deleteError } = useAdminDeleteUser();

  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);

  // Edit form state
  const [editBio, setEditBio] = useState('');
  const [editHourlyRate, setEditHourlyRate] = useState('');
  const [editServiceArea, setEditServiceArea] = useState('');
  const [editCareTypes, setEditCareTypes] = useState<string[]>([]);
  const [editSkills, setEditSkills] = useState<string[]>([]);

  useEffect(() => {
    if (onboarding) {
      setEditBio(onboarding.bio ?? '');
      setEditHourlyRate(String(onboarding.hourly_rate ?? ''));
      setEditServiceArea(onboarding.service_area ?? '');
      setEditCareTypes(onboarding.care_types ?? []);
      setEditSkills(onboarding.skills ?? []);
    }
  }, [onboarding]);

  const carer = carers?.find((c) => c.id === carerId);

  const openDocument = async (filePath: string) => {
    const { data, error } = await supabaseClient.storage
      .from('documents')
      .createSignedUrl(filePath, 60);
    if (error || !data?.signedUrl) return;
    window.open(data.signedUrl, '_blank');
  };

  const handleApprove = () => {
    if (carerId) {
      updateVerification({ user_id: carerId, is_verified: true }, {
        onSuccess: () => setIsApproveModalOpen(false),
      });
    }
  };

  const handleReject = (reason: string) => {
    if (carerId) {
      updateVerification({ user_id: carerId, is_verified: false }, {
        onSuccess: () => {
          console.log('Rejected with reason:', reason);
          setIsRejectModalOpen(false);
        },
      });
    }
  };

  const handleSaveProfile = () => {
    if (!carerId) return;
    updateProfile({
      user_id: carerId,
      bio: editBio,
      hourly_rate: editHourlyRate ? Number(editHourlyRate) : undefined,
      service_area: editServiceArea,
      care_types: editCareTypes,
      skills: editSkills,
    });
  };

  const handleToggleHide = () => {
    if (!carer || !carerId) return;
    toggleVisibility({ user_id: carerId, is_hidden: !carer.is_hidden });
  };

  const handleDelete = () => {
    if (!carerId) return;
    deleteUser(carerId, {
      onSuccess: () => {
        setIsDeleteModalOpen(false);
        navigate(Routes.ADMIN_CARERS);
      },
    });
  };

  const toggleChip = (value: string, list: string[], setList: (v: string[]) => void) => {
    setList(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
  };

  if (isLoading || isLoadingOnboarding) {
    return (
      <div className="flex flex-col gap-6 w-full animate-pulse">
        <div className="h-10 bg-gray-200 rounded-lg w-10" />
        <div className="h-32 bg-gray-200 rounded-lg" />
        <div className="h-64 bg-gray-200 rounded-lg" />
      </div>
    );
  }

  if (isError || !carer) {
    return (
      <div className="flex flex-col gap-6 w-full">
        <button onClick={() => navigate(Routes.ADMIN_CARERS)} className="w-10 h-10 rounded-[12px] bg-black flex items-center justify-center text-white hover:opacity-80 cursor-pointer">
          <img src="/assets/arrowLeft.svg" alt="back" style={{ width: '16.5px', height: '15px' }} />
        </button>
        <div className="text-red-500">Carer not found</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 w-full">
      {/* Top bar */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate(Routes.ADMIN_CARERS)}
          className="w-10 h-10 rounded-[12px] bg-black flex items-center justify-center text-white hover:opacity-80 cursor-pointer"
        >
          <img src="/assets/arrowLeft.svg" alt="back" style={{ width: '16.5px', height: '15px' }} />
        </button>

        <div className="flex items-center gap-3">
          {/* Delete */}
          <Button
            onClick={() => setIsDeleteModalOpen(true)}
            className="bg-white text-[#FF5861] border border-[#FF5861] hover:bg-[#FF5861] hover:text-white rounded-[12px] px-6"
          >
            Delete
          </Button>

          {/* Hide / Reinstate */}
          <Button
            onClick={handleToggleHide}
            disabled={isTogglingVisibility}
            className={`border-none hover:opacity-80 rounded-[12px] px-6 ${
              carer.is_hidden
                ? 'bg-[#00A96E] text-white'
                : 'bg-gray-200 text-gray-800'
            }`}
          >
            {carer.is_hidden ? 'Reinstate' : 'Hide'}
          </Button>

          {/* Verify / Reject */}
          {carer.is_verified ? (
            <Button
              onClick={() => setIsRejectModalOpen(true)}
              disabled={isVerifying}
              className="bg-[#FF5861] text-white border-none hover:opacity-80 rounded-[12px] px-8"
            >
              Reject
            </Button>
          ) : (
            <Button
              onClick={() => setIsApproveModalOpen(true)}
              disabled={isVerifying}
              className="bg-[#00A96E] text-white border-none hover:opacity-80 rounded-[12px] px-8"
            >
              Verify
            </Button>
          )}
        </div>
      </div>

      {/* Hidden banner */}
      {carer.is_hidden && (
        <div className="bg-gray-100 border border-gray-300 rounded-xl px-4 py-3 text-sm text-gray-600 font-medium">
          This account is <strong>hidden</strong> — the carer does not appear in family search results.
        </div>
      )}

      {/* Profile info */}
      <div className="bg-[#F6F6F4] rounded-xl p-4 sm:p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          {onboarding?.photo_url && (
            <div>
              <label className="text-sm text-[#9CA3AF] block mb-1">Photo</label>
              <img src={onboarding.photo_url} alt="Carer photo" className="w-full h-64 object-contain rounded-lg mt-2" />
            </div>
          )}

          <div className="space-y-6">
            <div>
              <label className="text-sm text-[#9CA3AF] block">Carer Name</label>
              <div className="bg-white rounded-[120px] mt-2 px-4 py-2 inline-block">
                <p className="text-sm font-bold">
                  {carer.first_name || carer.last_name
                    ? `${carer.first_name ?? ''} ${carer.last_name ?? ''}`.trim()
                    : carer.email.split('@')[0]}
                </p>
              </div>
            </div>
            <div>
              <label className="text-sm text-[#9CA3AF] block mb-1">Email</label>
              <p className="bg-white text-sm font-bold rounded-[120px] mt-2 px-4 py-2 inline-block max-w-full break-all">
                {carer.email}
              </p>
            </div>
            {carer.phone_number && (
              <div>
                <label className="text-sm text-[#9CA3AF] block mb-1">Phone Number</label>
                <p className="bg-white text-sm font-bold rounded-[120px] mt-2 px-4 py-2 inline-block">
                  {carer.phone_number}
                </p>
              </div>
            )}
            <div className="flex flex-wrap gap-4">
              {carer.gender && (
                <div>
                  <label className="text-sm text-[#9CA3AF] block mb-1">Gender</label>
                  <p className="bg-white text-sm font-bold rounded-[120px] mt-2 px-4 py-2 inline-block">
                    {carer.gender}
                  </p>
                </div>
              )}
              {carer.city_town && (
                <div>
                  <label className="text-sm text-[#9CA3AF] block mb-1">City / Town</label>
                  <p className="bg-white text-sm font-bold rounded-[120px] mt-2 px-4 py-2 inline-block">
                    {carer.city_town}
                  </p>
                </div>
              )}
              {carer.postcode && (
                <div>
                  <label className="text-sm text-[#9CA3AF] block mb-1">Postcode</label>
                  <p className="bg-white text-sm font-bold rounded-[120px] mt-2 px-4 py-2 inline-block">
                    {carer.postcode}
                  </p>
                </div>
              )}
            </div>
            <div>
              <label className="text-sm text-[#9CA3AF] block mb-1">Account Created</label>
              <p className="bg-white text-sm font-bold rounded-[120px] mt-2 px-4 py-2 inline-block">
                {dayjs(carer.created_at).format('ddd, DD MMM YYYY — HH:mm')}
              </p>
            </div>
            <div>
              <label className="text-sm text-[#9CA3AF] block mb-1">Status</label>
              <p className="bg-white text-sm font-bold rounded-[120px] mt-2 px-4 py-2 inline-block">
                <span className={carer.is_verified ? 'text-[#00A96E]' : 'text-[#FF5861]'}>
                  {carer.is_verified ? 'Verified' : 'Not Verified'}
                </span>
              </p>
            </div>
          </div>
        </div>

        {onboarding && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            <div className="space-y-4">
              {onboarding.bio && (
                <div>
                  <label className="text-sm text-[#9CA3AF] block mb-1">Bio</label>
                  <p className="text-sm">{onboarding.bio}</p>
                </div>
              )}
              {onboarding.care_types && onboarding.care_types.length > 0 && (
                <div>
                  <label className="text-sm text-[#9CA3AF] block mb-1">Care Types</label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {onboarding.care_types.map((type: string) => (
                      <span key={type} className="bg-white text-sm font-bold rounded-[120px] px-4 py-2">
                        {formatCareType(type)}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {onboarding.skills && onboarding.skills.length > 0 && (
                <div>
                  <label className="text-sm text-[#9CA3AF] block mb-1">Skills</label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {onboarding.skills.map((skill: string) => (
                      <span key={skill} className="bg-white text-sm font-bold rounded-[120px] px-4 py-2">
                        {formatSkill(skill)}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="space-y-4">
              {Number(onboarding.hourly_rate) > 0 && (
                <div>
                  <label className="text-sm text-[#9CA3AF] block mb-1">Hourly Rate</label>
                  <p className="bg-white text-sm font-bold rounded-[120px] mt-2 px-4 py-2 inline-block">
                    £{onboarding.hourly_rate}
                  </p>
                </div>
              )}
              <div>
                <label className="text-sm text-[#9CA3AF] block mb-1">Service Area</label>
                <p className="bg-white text-sm font-bold rounded-[120px] mt-2 px-4 py-2 inline-block">
                  {onboarding.service_area}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Document buttons */}
      {onboarding && (onboarding.dbs_document_url || onboarding.insurance_document_url) && (
        <div className="flex flex-col sm:flex-row gap-3">
          {onboarding.dbs_document_url && (
            <Button onClick={() => openDocument(onboarding.dbs_document_url!)} className="bg-black text-white border-none hover:opacity-80 rounded-[12px] px-8 w-full sm:w-auto">
              Review Enhanced DBS
            </Button>
          )}
          {onboarding.insurance_document_url && (
            <Button onClick={() => openDocument(onboarding.insurance_document_url!)} className="bg-black text-white border-none hover:opacity-80 rounded-[12px] px-8 w-full sm:w-auto">
              Review Insurance
            </Button>
          )}
        </div>
      )}

      {/* ── Edit Profile Panel ── */}
      <div className="border border-gray-200 rounded-xl overflow-hidden">
        <button
          type="button"
          onClick={() => setIsEditOpen((v) => !v)}
          className="w-full flex items-center justify-between px-5 py-4 bg-white text-left font-semibold text-sm cursor-pointer hover:bg-gray-50 transition-colors"
        >
          <span>Edit profile — factual corrections only</span>
          <svg
            className={`w-4 h-4 text-gray-500 transition-transform duration-300 ${isEditOpen ? 'rotate-180' : ''}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        <div className={`grid transition-all duration-300 ${isEditOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
          <div className="overflow-hidden">
            <div className="p-5 flex flex-col gap-5 bg-[#F6F6F4]">
              {isSaveSuccess && (
                <Alert status="success" className="bg-[#FED32C] text-black border-none text-sm">
                  Profile updated successfully.
                </Alert>
              )}
              {isSaveError && (
                <Alert status="error" className="text-sm">
                  {(saveError as Error)?.message || 'Failed to save changes.'}
                </Alert>
              )}

              {/* Bio */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-gray-600">Bio</label>
                <textarea
                  value={editBio}
                  onChange={(e) => setEditBio(e.target.value)}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-black text-sm bg-white"
                  placeholder="Carer bio..."
                />
              </div>

              {/* Hourly Rate */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-gray-600">Hourly Rate (£)</label>
                <input
                  type="number"
                  value={editHourlyRate}
                  onChange={(e) => setEditHourlyRate(e.target.value)}
                  min={0}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-black text-sm bg-white"
                  placeholder="e.g. 18"
                />
              </div>

              {/* Service Area */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-gray-600">Service Area (postcode)</label>
                <input
                  type="text"
                  value={editServiceArea}
                  onChange={(e) => setEditServiceArea(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-black text-sm bg-white"
                  placeholder="e.g. N1"
                />
              </div>

              {/* Care Types */}
              <div className="flex flex-col gap-2">
                <label className="text-xs font-medium text-gray-600">Care Types (categories)</label>
                <div className="flex flex-wrap gap-2">
                  {CARE_TYPE_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => toggleChip(opt.value, editCareTypes, setEditCareTypes)}
                      className={`px-3 py-1.5 rounded-full text-sm border transition-colors cursor-pointer ${
                        editCareTypes.includes(opt.value)
                          ? 'bg-black text-white border-black'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-gray-500'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Skills — grouped by category */}
              <div className="flex flex-col gap-3">
                <label className="text-xs font-medium text-gray-600">Skills</label>
                {SKILL_CATEGORIES.map((cat) => (
                  <div key={cat.value} className="flex flex-col gap-1.5">
                    <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide">{cat.label}</p>
                    <div className="flex flex-wrap gap-2">
                      {cat.skills.map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => toggleChip(opt.value, editSkills, setEditSkills)}
                          className={`px-3 py-1.5 rounded-full text-sm border transition-colors cursor-pointer ${
                            editSkills.includes(opt.value)
                              ? 'bg-black text-white border-black'
                              : 'bg-white text-gray-700 border-gray-300 hover:border-gray-500'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <p className="text-xs text-gray-400 italic">Use only for factual corrections.</p>

              <Button
                onClick={handleSaveProfile}
                disabled={isSavingProfile}
                className="bg-black text-white border-none hover:opacity-80 rounded-[12px] w-full sm:w-auto self-start px-8"
              >
                {isSavingProfile ? 'Saving...' : 'Save changes'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <ApproveCarerModal
        isOpen={isApproveModalOpen}
        onClose={() => setIsApproveModalOpen(false)}
        onConfirm={handleApprove}
        carerName={carer.email.split('@')[0]}
        isLoading={isVerifying}
      />
      <RejectCarerModal
        isOpen={isRejectModalOpen}
        onClose={() => setIsRejectModalOpen(false)}
        onConfirm={handleReject}
        carerName={carer.email.split('@')[0]}
        isLoading={isVerifying}
      />
      <DeleteUserModal
        isOpen={isDeleteModalOpen}
        onClose={() => isDeleting ? null : setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        userName={carer.email}
        userType="carer"
        isLoading={isDeleting}
        error={isDeleteError ? (deleteError as Error)?.message : null}
      />
    </div>
  );
};

export default AdminCarerDetailsPage;
